import { spaces as predefinedSpaces } from './spaces/spaces';
import { getWorkplaceStyleById } from './workplaceStyles.js';
import { getSpaceTypeById } from './spaceTypes.js';
import { flatten, removeDuplicates, getFirstAndLast } from 'utils/arrays';
import { roundThousandAndAddThousandSeparator } from 'utils/numbers';
import { generateUniqueId } from 'utils/objects';
import cloneDeep from 'lodash/cloneDeep';

// Format is totalHeadcount in number, mobility in percentage based on 100% like 20, mobilityRatio has the format 1.75
const getInSiteParameters = (totalHeadcount, remote, mobility, mobilityRatio, headcount) => {
  const remoteHeadCount = Math.round((totalHeadcount * remote) / 100);
  const inOfficeHeadCount = totalHeadcount - remoteHeadCount;
  const mobilityHeadcount = Math.round((totalHeadcount * mobility) / 100);
  const assignedSeats = inOfficeHeadCount - mobilityHeadcount;
  const mobilitySeats = Math.round(mobilityHeadcount / mobilityRatio);
  const totalSeats = assignedSeats + mobilitySeats;

  return {
    totalHeadcount,
    remoteHeadCount,
    inOfficeHeadCount,
    remote,
    mobility,
    mobilityHeadcount,
    mobilityRatio,
    assignedSeats,
    mobilitySeats,
    totalSeats,
    headcount,
  };
};

const getInSiteSpaces = (inSiteParams, country) =>
  predefinedSpaces
    .filter((space) => space.countries.includes(country))
    .map(({ countries, ...space }) => ({
      ...space,
      size: space.size[country],
      imageUrl: space.imageUrl[country],
      quantity: space.quantity[country],
    }))
    .map((space) => ({
      ...space,
      quantity: space.quantity.map((workplace) => ({
        id: workplace.id,
        value: workplace.fx(inSiteParams),
      })),
    }));

const getWorkplacesfromSpaces = (spaces, country) => {
  const workplaceIds = spaces.map((space) => space.quantity.map((workplace) => workplace.id));

  const flattenworkplaceIds = flatten(workplaceIds);
  const uniqueworkplaceIds = removeDuplicates(flattenworkplaceIds);

  return uniqueworkplaceIds.map((workplaceId) => ({
    ...getWorkplaceStyleById(workplaceId, country),
    spaceTypes: [],
    customSpaces: [],
  }));
};

const addSpacesToWorkplaces = (inSiteSpaces, workplaces) => {
  inSiteSpaces.forEach((space) => {
    space.quantity.forEach((workplaceQuantity) => {
      const workplace = workplaces.find((wp) => wp.id === workplaceQuantity.id);

      let spaceType = workplace.spaceTypes.find((spType) => spType.id === space.spaceTypeId);
      if (!spaceType) {
        spaceType = { ...getSpaceTypeById(space.spaceTypeId), spaces: [] };
        workplace.spaceTypes.push(spaceType);
      }

      spaceType.spaces.push({ ...space, quantity: workplaceQuantity.value });
    });
  });
};

const updateWorkplaceRentableSpace = (workplace, inSiteParameters) => {
  const usableSquareFeet =
    workplace.netProgramSqFootage * (1 + workplace.circulationMultiplier / 100);
  const rentableSquareFeet = usableSquareFeet * (1 + workplace.buildingGrossUpFactor / 100);

  workplace.usableSquareFeet = Math.round(usableSquareFeet);
  workplace.rentableSquareFeet = Math.round(rentableSquareFeet);
  workplace.rentableSquareFeetPerHead =
    workplace.totalHeadcount !== 0 && inSiteParameters.totalSeats !== 0
      ? Math.round(rentableSquareFeet / inSiteParameters.totalSeats)
      : 0;
};

const calculateWorkplaceRentableSpace = (workplaces, inSiteParameters) => {
  workplaces.forEach((workplace) => {
    let nsf = 0;
    workplace.spaceTypes.forEach((spaceType) => {
      spaceType.spaces.forEach((space) => {
        nsf += space.quantity * space.size.width * space.size.height;
      });
    });
    workplace.customSpaces.forEach((customSpace) => {
      nsf += customSpace.quantity * customSpace.width * customSpace.height;
    });

    workplace.totalHeadcount = inSiteParameters.totalHeadcount;
    workplace.netProgramSqFootage = nsf;
    updateWorkplaceRentableSpace(workplace, inSiteParameters);
  });
};

export const getInsiteWorkplaces = (
  totalHeadcount,
  remote,
  mobility,
  mobilityRatio,
  country,
  inOffice,
  headcount,
) => {
  const inSiteParameters = getInSiteParameters(
    totalHeadcount,
    remote,
    mobility,
    mobilityRatio,
    headcount,
  );

  const inSiteSpaces = getInSiteSpaces(inSiteParameters, country);
  const workplaces = getWorkplacesfromSpaces(inSiteSpaces, country);

  addSpacesToWorkplaces(inSiteSpaces, workplaces);
  calculateWorkplaceRentableSpace(workplaces, inSiteParameters);
  return workplaces;
};

export const updateWorkPlaces = (workplaces, totalHeadcount, remote, mobility, mobilityRatio) => {
  const inSiteParameters = getInSiteParameters(totalHeadcount, remote, mobility, mobilityRatio);
  workplaces.forEach((workplace) => {
    updateWorkplaceRentableSpace(workplace, inSiteParameters);
  });
};

export const updateWorkplaceSpaces = (
  workplaces,
  totalHeadcount,
  remote,
  mobility,
  mobilityRatio,
) => {
  const newWorkplaces = [...workplaces];

  const inSiteParameters = getInSiteParameters(totalHeadcount, remote, mobility, mobilityRatio);

  calculateWorkplaceRentableSpace(newWorkplaces, inSiteParameters);

  return newWorkplaces;
};

export const updateSpaceType = (spaces, workplacestyleSelected, updatedSpaceType) => {
  const newSpaces = cloneDeep(spaces);
  const spaceSelected = newSpaces.find(
    (workplaceStyle) => workplaceStyle.id === workplacestyleSelected,
  );
  const spaceIndex = spaceSelected.spaceTypes.findIndex(
    (spaceType) => spaceType.id === updatedSpaceType.id,
  );

  if (spaceIndex > -1) spaceSelected.spaceTypes[spaceIndex] = { ...updatedSpaceType };

  return newSpaces;
};

export const updateCustomSpace = (spaces, workplacestyleSelected, updatedCustomSpace) => {
  const newSpaces = cloneDeep(spaces);
  const spaceSelected = newSpaces.find(
    (workplaceStyle) => workplaceStyle.id === workplacestyleSelected,
  );

  const customSpaceIndex = spaceSelected.customSpaces.findIndex(
    (customSpace) => customSpace.id === updatedCustomSpace.id,
  );

  if (customSpaceIndex > -1) {
    newSpaces.forEach(
      (space) => (space.customSpaces[customSpaceIndex] = { ...updatedCustomSpace }),
    );
  } else {
    newSpaces.forEach((space) => space.customSpaces.unshift({ ...updatedCustomSpace }));
  }

  return newSpaces;
};

export const removeCustomSpace = (spaces, workplacestyleSelected, customSpaceToRemove) => {
  const newSpaces = cloneDeep(spaces);
  const spaceSelected = newSpaces.find(
    (workplaceStyle) => workplaceStyle.id === workplacestyleSelected,
  );
  const customSpaceIndex = spaceSelected.customSpaces.findIndex(
    (customSpace) => customSpace.id === customSpaceToRemove.id,
  );

  if (customSpaceIndex > -1) {
    newSpaces.forEach((space) => space.customSpaces.splice(customSpaceIndex, 1));
  }

  return newSpaces;
};

export const getFirstAndLastRSF = (inSiteSpaces, formatOptions) => {
  return getFirstAndLast(
    inSiteSpaces.map((space) =>
      roundThousandAndAddThousandSeparator(space.rentableSquareFeet, formatOptions),
    ),
  );
};

export const getNewCustomSpace = () => {
  return {
    id: generateUniqueId('customSpace'),
    name: '',
    width: 0,
    height: 0,
    quantity: 0,
  };
};

export const updateSettings = (spaces, settings) =>
  spaces.map((space) => {
    if (space.id === settings.workplacestyleSelected) {
      return {
        ...space,
        circulationMultiplier: settings.circulationMultiplier,
        buildingGrossUpFactor: settings.buildingGrossUpFactor,
      };
    } else return space;
  });
